/**
 * Pure helper for the "player is out" modal (elimination OR resignation).
 *
 * The wire protocol has no dedicated "out" event — the client learns of it by
 * watching the game state's `eliminated[]` grow across pushed updates. BOTH
 * combat eliminations and resignations add the player to `eliminated`, but they
 * carry different events:
 *   - elimination → an `{ kind: 'elimination', eliminator, eliminated }` event
 *   - resignation → a `{ kind: 'resign', player }` event (no eliminator)
 *
 * This diffs the previous and current `eliminated` lists and classifies each
 * NEW entry. Kept free of React/RN so it's trivially unit-testable. Slots are
 * carried on the wire as strings (PlayerId); surfaced as numbers for the UI.
 */
import type { GameEvent, TeamId } from '@warcarp/api-types'

/** How long elimination/resignation notices remain visible before closing. */
export const PLAYER_OUT_AUTO_DISMISS_MS = 4000

export interface PlayerOutNotice {
  /** Slot of the player who just left the game. */
  slot: number
  /** Why they're out. */
  reason: 'eliminated' | 'resigned'
  /** Eliminator's slot (for 'eliminated'); null for resignations / unknown. */
  bySlot: number | null
  /**
   * Team games: this player's exit eliminated their whole team (a
   * `team-eliminated` event names them as `lastPlayer`). Absent otherwise.
   */
  teamOut?: TeamId
}

/**
 * Keep a player-out notice one-shot for the lifetime of a game screen.
 *
 * A stale frame can briefly make `eliminated` shrink and then grow again, and
 * the same event can also arrive through HTTP and WS. The state baseline guard
 * handles the normal path; this second guard prevents a transient rollback or
 * duplicate delivery from showing the same player's banner twice.
 */
export function filterUnseenPlayerOutNotices(
  notices: readonly PlayerOutNotice[],
  alreadyShownSlots: ReadonlySet<number>,
): PlayerOutNotice[] {
  const seen = new Set(alreadyShownSlots)
  const unseen: PlayerOutNotice[] = []
  for (const notice of notices) {
    if (seen.has(notice.slot)) continue
    seen.add(notice.slot)
    unseen.push(notice)
  }
  return unseen
}

/**
 * Returns the players present in `eliminated` but NOT in `prevEliminated` —
 * i.e. the ones that just went out — classified as eliminated (with attacker)
 * or resigned. Seed `prevEliminated` with the current list on first load so
 * players already out when the viewer arrives don't fire the modal.
 */
export function newPlayerOutNotices(
  prevEliminated: readonly string[],
  eliminated: readonly string[],
  events: readonly GameEvent[],
): PlayerOutNotice[] {
  const prev = new Set(prevEliminated)
  const reversed = [...events].reverse() // newest-first
  return eliminated
    .filter((pid) => !prev.has(pid))
    .map((pid): PlayerOutNotice => {
      const teamOut = reversed.find(
        (e): e is Extract<GameEvent, { kind: 'team-eliminated' }> =>
          e.kind === 'team-eliminated' && e.lastPlayer === pid,
      )?.team
      return { ...classifyPlayerOut(pid, reversed), ...(teamOut ? { teamOut } : {}) }
    })
}

/** Eliminated in combat (with the attacker) or resigned. */
function classifyPlayerOut(pid: string, reversed: readonly GameEvent[]): PlayerOutNotice {
  const elim = reversed.find(
    (e): e is Extract<GameEvent, { kind: 'elimination' }> =>
      e.kind === 'elimination' && e.eliminated === pid,
  )
  if (elim) {
    return { slot: Number(pid), reason: 'eliminated', bySlot: Number(elim.eliminator) }
  }
  const resign = reversed.find(
    (e): e is Extract<GameEvent, { kind: 'resign' }> =>
      e.kind === 'resign' && e.player === pid,
  )
  if (resign) {
    return { slot: Number(pid), reason: 'resigned', bySlot: null }
  }
  // In `eliminated` but no recognised event — treat as a plain elimination.
  return { slot: Number(pid), reason: 'eliminated', bySlot: null }
}

export interface PlayerOutBaselineResult {
  /** The baseline to store for the next tick (always the current list). */
  baseline: string[]
  /** Notices to enqueue this tick (empty on the seeding call). */
  notices: PlayerOutNotice[]
}

/**
 * One step of the game screen's elimination-modal state machine.
 *
 * The FIRST call (prevEliminated === null) SEEDS the baseline and emits no
 * notices, so eliminations already present when the viewer opened the game
 * don't fire the modal. Every later call diffs against the stored baseline.
 *
 * Split out from the component so the seed-then-diff contract is unit-testable:
 * the screen's effect must invoke this the moment state first loads (not lazily
 * on the first `eliminated` change), otherwise the very first elimination of a
 * game is swallowed as the baseline and its modal never shows.
 */
export function advancePlayerOutBaseline(
  prevEliminated: readonly string[] | null,
  eliminated: readonly string[],
  events: readonly GameEvent[],
): PlayerOutBaselineResult {
  if (prevEliminated === null) {
    return { baseline: [...eliminated], notices: [] }
  }
  return {
    baseline: [...eliminated],
    notices: newPlayerOutNotices(prevEliminated, eliminated, events),
  }
}
